import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminBulkApi, adminDashboardApi, adminSettingsApi } from '../api/operations';
import { adminLogsApi, adminWebhooksApi } from '../api/platform';
import { Link } from 'react-router-dom';
import type {
  AuditAction,
  AuditLogQueryDto,
  BulkPriceUpdateDto,
  LogLevelType,
  LogQueryDto,
  LoginAttemptQueryDto,
  LoginAttemptResult,
  WebhookSubscriptionUpsertDto,
} from '../api/types';
import {
  auditActionValues,
  logLevelTypeValues,
  loginAttemptResultValues,
} from '../api/types';
import { Column, DataTable, SearchInput, SelectFilter, Toolbar } from '../components/DataTable';
import { PageHeader } from '../components/Shell';
import {
  Badge,
  ConfirmDialog,
  Field,
  FormErrors,
  Modal,
  PageLoader,
  Pagination,
  Spinner,
} from '../components/ui';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { formatDateTime, number, truncate } from '../lib/format';
import { bulkStatusLabels, labelOf } from '../lib/labels';
import { P } from '../lib/permissions';
import { ImageUploadField } from '../components/ImageUploadField';
import {
  Store,
  MessageSquare,
  CreditCard,
  Mail,
  Share2,
  Globe,
  CheckCircle2,
  Send,
  Copy,
  Check,
  ExternalLink,
  ShieldCheck,
  Megaphone,
  Truck,
  Tag,
  Bell,
} from 'lucide-react';

/* ============================== TOPLU ISLEMLER ============================== */

export function BulkOperationsPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [page, setPage] = useState(1);
  const [priceOpen, setPriceOpen] = useState(false);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [priceForm, setPriceForm] = useState<BulkPriceUpdateDto>({
    productIds: [],
    adjustmentType: 'percentage',
    value: -10,
    updateCompareAtPrice: false,
    rounding: '0.99',
  });

  const operations = useQuery({
    queryKey: ['admin', 'bulk', page],
    queryFn: () => adminBulkApi.search({ page, pageSize: 20 }),
    placeholderData: keepPreviousData,
    // Calisan islem varsa liste kendini tazeler.
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some(
        (item) => item.status === 'Running' || item.status === 'Queued',
      )
        ? 4000
        : false,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'bulk'] });

  const importProducts = useMutation({
    mutationFn: (file: File) => adminBulkApi.importProducts(file),
    onSuccess: () => {
      toast.success('Ürün içe aktarımı başlatıldı.');
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'İçe aktarım başlatılamadı.'),
  });

  const importInventory = useMutation({
    mutationFn: (file: File) => adminBulkApi.importInventory(file),
    onSuccess: () => {
      toast.success('Stok içe aktarımı başlatıldı.');
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'İçe aktarım başlatılamadı.'),
  });

  const updatePrices = useMutation({
    mutationFn: () => adminBulkApi.updatePrices(priceForm),
    onSuccess: () => {
      toast.success('Toplu fiyat güncellemesi başlatıldı.');
      setPriceOpen(false);
      void invalidate();
    },
  });

  const cancel = useMutation({
    mutationFn: (publicId: string) => adminBulkApi.cancel(publicId),
    onSuccess: (message) => {
      toast.success(message ?? 'İşlem iptal edildi.');
      setCancelId(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'İptal edilemedi.'),
  });

  const columns: Column<NonNullable<typeof operations.data>['items'][number]>[] = [
    {
      key: 'type',
      header: 'İşlem',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">{row.operationType}</p>
          <p className="truncate text-xs text-ink-400">
            {row.inputFileName ?? row.publicId.slice(0, 8)}
          </p>
        </div>
      ),
    },
    {
      key: 'progress',
      header: 'İlerleme',
      cell: (row) => (
        <div className="min-w-32">
          <div className="h-1.5 overflow-hidden rounded-full bg-ink-100">
            <div
              className={`h-full ${row.errorCount > 0 ? 'bg-amber-500' : 'bg-brand-500'}`}
              style={{ width: `${Math.min(100, row.progressPercentage)}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-ink-500">
            {number(row.processedCount)} / {number(row.totalCount)}
            {row.errorCount > 0 ? ` · ${number(row.errorCount)} hata` : ''}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Durum',
      cell: (row) => <Badge label={labelOf(bulkStatusLabels, row.status)} />,
    },
    {
      key: 'created',
      header: 'Başlangıç',
      hideOnMobile: true,
      cell: (row) => (
        <span className="text-ink-500">{formatDateTime(row.startedAt ?? row.createdAt)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <div className="flex justify-end gap-1">
          {row.resultFileUrl ? (
            <a href={row.resultFileUrl} className="btn-ghost btn-sm" download>
              Sonucu indir
            </a>
          ) : null}
          {row.errorReportUrl ? (
            <a href={row.errorReportUrl} className="btn-ghost btn-sm text-amber-700" download>
              Hata raporu
            </a>
          ) : null}
          {(row.status === 'Running' || row.status === 'Queued') && can(P.products.bulk) ? (
            <button
              type="button"
              className="btn-ghost btn-sm text-red-600"
              onClick={() => setCancelId(row.publicId)}
            >
              İptal
            </button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Toplu işlemler"
        description="CSV içe/dışa aktarma ve toplu fiyat güncellemesi"
        actions={
          can(P.products.bulk) ? (
            <>
              <label className="btn-secondary cursor-pointer">
                {importProducts.isPending ? <Spinner className="h-4 w-4" /> : null}
                Ürün CSV yükle
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) importProducts.mutate(file);
                    event.target.value = '';
                  }}
                />
              </label>

              <label className="btn-secondary cursor-pointer">
                {importInventory.isPending ? <Spinner className="h-4 w-4" /> : null}
                Stok CSV yükle
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) importInventory.mutate(file);
                    event.target.value = '';
                  }}
                />
              </label>

              <button type="button" className="btn-primary" onClick={() => setPriceOpen(true)}>
                Toplu fiyat güncelle
              </button>
            </>
          ) : null
        }
      />

      <DataTable
        columns={columns}
        rows={operations.data?.items ?? []}
        rowKey={(row) => row.publicId}
        isLoading={operations.isLoading}
        error={operations.error}
        onRetry={() => void operations.refetch()}
        emptyMessage="Toplu işlem kaydı yok."
        footer={
          operations.data ? (
            <Pagination
              page={operations.data.page}
              totalPages={operations.data.totalPages}
              totalCount={operations.data.totalCount}
              onChange={setPage}
            />
          ) : null
        }
      />

      {/* Hata ozetleri */}
      {(operations.data?.items ?? [])
        .filter((item) => item.errorSummary.length > 0)
        .slice(0, 3)
        .map((item) => (
          <section key={item.publicId} className="card mt-4 p-5">
            <h2 className="mb-2 text-sm font-semibold text-ink-900">
              {item.operationType} — hata özeti
            </h2>
            <ul className="space-y-1 text-sm text-red-700">
              {item.errorSummary.slice(0, 10).map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
            {item.failureReason ? (
              <p className="mt-2 text-sm text-red-800">{item.failureReason}</p>
            ) : null}
          </section>
        ))}

      <Modal
        open={priceOpen}
        title="Toplu fiyat güncelle"
        description="Filtreye uyan tüm varyantların fiyatı değiştirilir."
        onClose={() => setPriceOpen(false)}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setPriceOpen(false)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => updatePrices.mutate()}
              disabled={updatePrices.isPending}
            >
              {updatePrices.isPending ? <Spinner className="h-4 w-4" /> : null}
              Başlat
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <FormErrors error={updatePrices.error} />

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Ayarlama türü" htmlFor="bp-type" required>
              <select
                id="bp-type"
                className="field"
                value={priceForm.adjustmentType}
                onChange={(event) =>
                  setPriceForm((c) => ({ ...c, adjustmentType: event.target.value }))
                }
              >
                <option value="percentage">Yüzde</option>
                <option value="amount">Tutar</option>
                <option value="set">Sabit fiyat</option>
              </select>
            </Field>

            <Field
              label="Değer"
              htmlFor="bp-value"
              required
              hint="Negatif değer indirim, pozitif zam."
            >
              <input
                id="bp-value"
                type="number"
                step="0.01"
                className="field"
                value={priceForm.value}
                onChange={(event) => setPriceForm((c) => ({ ...c, value: Number(event.target.value) }))}
              />
            </Field>

            <Field label="Yuvarlama" htmlFor="bp-round">
              <select
                id="bp-round"
                className="field"
                value={priceForm.rounding}
                onChange={(event) => setPriceForm((c) => ({ ...c, rounding: event.target.value }))}
              >
                <option value="none">Yuvarlama yok</option>
                <option value="0.99">,99 ile bitir</option>
                <option value="0.95">,95 ile bitir</option>
                <option value="integer">Tam sayıya yuvarla</option>
              </select>
            </Field>

            <Field label="Kategori kimliği" htmlFor="bp-category" hint="Boşsa tüm kategoriler.">
              <input
                id="bp-category"
                type="number"
                className="field"
                value={priceForm.categoryId ?? ''}
                onChange={(event) =>
                  setPriceForm((c) => ({
                    ...c,
                    categoryId: event.target.value ? Number(event.target.value) : null,
                  }))
                }
              />
            </Field>

            <Field label="Marka kimliği" htmlFor="bp-brand" hint="Boşsa tüm markalar.">
              <input
                id="bp-brand"
                type="number"
                className="field"
                value={priceForm.brandId ?? ''}
                onChange={(event) =>
                  setPriceForm((c) => ({
                    ...c,
                    brandId: event.target.value ? Number(event.target.value) : null,
                  }))
                }
              />
            </Field>

            <Field
              label="Ürün kimlikleri"
              htmlFor="bp-products"
              hint="Virgülle ayırın; verilirse filtre yerine bunlar kullanılır."
            >
              <input
                id="bp-products"
                className="field font-mono text-sm"
                value={priceForm.productIds.join(', ')}
                onChange={(event) =>
                  setPriceForm((c) => ({
                    ...c,
                    productIds: event.target.value
                      .split(',')
                      .map((raw) => Number(raw.trim()))
                      .filter((value) => Number.isFinite(value) && value > 0),
                  }))
                }
              />
            </Field>
          </div>

          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand-600"
              checked={priceForm.updateCompareAtPrice}
              onChange={(event) =>
                setPriceForm((c) => ({ ...c, updateCompareAtPrice: event.target.checked }))
              }
            />
            Üzeri çizili fiyatı da güncelle
          </label>
        </div>
      </Modal>

      <ConfirmDialog
        open={cancelId !== null}
        title="İşlemi iptal et"
        message="Devam eden toplu işlem durdurulur. İşlenmiş satırlar geri alınmaz."
        confirmLabel="İptal et"
        danger
        busy={cancel.isPending}
        onCancel={() => setCancelId(null)}
        onConfirm={() => cancelId && cancel.mutate(cancelId)}
      />
    </div>
  );
}

/* ================================= WEBHOOK ================================= */

const WEBHOOK_TOPICS = [
  'order.created',
  'order.paid',
  'order.cancelled',
  'order.fulfilled',
  'order.delivered',
  'refund.created',
  'refund.completed',
  'return.requested',
  'product.created',
  'product.updated',
  'inventory.low',
  'customer.created',
];

const emptyWebhook: WebhookSubscriptionUpsertDto = {
  name: '',
  topics: [],
  targetUrl: '',
  isActive: true,
  maxRetryCount: 5,
};

export function WebhooksPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [editing, setEditing] = useState<{
    id: number | null;
    form: WebhookSubscriptionUpsertDto;
  } | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deliveriesFor, setDeliveriesFor] = useState<number | null>(null);
  const [issuedSecret, setIssuedSecret] = useState<string | null>(null);

  const webhooks = useQuery({
    queryKey: ['admin', 'webhooks'],
    queryFn: () => adminWebhooksApi.getAll(),
  });

  const deliveries = useQuery({
    queryKey: ['admin', 'webhook-deliveries', deliveriesFor],
    queryFn: () => adminWebhooksApi.deliveries(deliveriesFor!, { pageSize: 30 }),
    enabled: deliveriesFor !== null,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'webhooks'] });

  const save = useMutation({
    mutationFn: () =>
      editing!.id === null
        ? adminWebhooksApi.create(editing!.form)
        : adminWebhooksApi.update(editing!.id, editing!.form),
    onSuccess: (subscription) => {
      toast.success('Webhook kaydedildi.');
      if (subscription.secret) setIssuedSecret(subscription.secret);
      setEditing(null);
      void invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminWebhooksApi.remove(id),
    onSuccess: (message) => {
      toast.success(message ?? 'Webhook silindi.');
      setDeleteId(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Silinemedi.'),
  });

  if (webhooks.isLoading) return <PageLoader />;

  return (
    <div>
      <PageHeader
        title="Webhook"
        description="Olaylar HMAC-SHA256 ile imzalanır ve X-Webhook-Signature başlığında gönderilir."
        actions={
          can(P.settings.manage) ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => setEditing({ id: null, form: emptyWebhook })}
            >
              Yeni webhook
            </button>
          ) : null
        }
      />

      <ul className="space-y-4">
        {(webhooks.data ?? []).map((webhook) => (
          <li key={webhook.id} className="card p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-ink-900">{webhook.name}</p>
                <p className="truncate font-mono text-xs text-ink-500">{webhook.targetUrl}</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {webhook.topics.map((topic) => (
                    <span key={topic} className="badge bg-ink-100 font-mono text-[10px] text-ink-600">
                      {topic}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {webhook.isActive ? (
                  <span className="badge bg-emerald-50 text-emerald-700">Aktif</span>
                ) : (
                  <span className="badge bg-ink-100 text-ink-600">Pasif</span>
                )}
                {webhook.consecutiveFailureCount > 0 ? (
                  <span className="badge bg-red-50 text-red-700">
                    {webhook.consecutiveFailureCount} ardışık hata
                  </span>
                ) : null}

                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={() => setDeliveriesFor(webhook.id)}
                >
                  Gönderimler
                </button>

                {can(P.settings.manage) ? (
                  <>
                    <button
                      type="button"
                      className="btn-ghost btn-sm"
                      onClick={() =>
                        setEditing({
                          id: webhook.id,
                          form: {
                            name: webhook.name,
                            topics: webhook.topics,
                            targetUrl: webhook.targetUrl,
                            isActive: webhook.isActive,
                            maxRetryCount: 5,
                          },
                        })
                      }
                    >
                      Düzenle
                    </button>
                    <button
                      type="button"
                      className="btn-ghost btn-sm text-red-600"
                      onClick={() => setDeleteId(webhook.id)}
                    >
                      Sil
                    </button>
                  </>
                ) : null}
              </div>
            </div>

            <p className="mt-3 text-xs text-ink-400">
              Son tetikleme: {formatDateTime(webhook.lastTriggeredAt)} · Son başarı:{' '}
              {formatDateTime(webhook.lastSuccessAt)}
            </p>
          </li>
        ))}
      </ul>

      {(webhooks.data ?? []).length === 0 ? (
        <p className="rounded-xl border border-dashed border-ink-200 px-6 py-12 text-center text-sm text-ink-500">
          Webhook aboneliği yok.
        </p>
      ) : null}

      <Modal
        open={editing !== null}
        title={editing?.id === null ? 'Yeni webhook' : 'Webhook düzenle'}
        onClose={() => setEditing(null)}
        size="lg"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => save.mutate()}
              disabled={
                save.isPending ||
                !editing?.form.name ||
                !editing?.form.targetUrl ||
                editing.form.topics.length === 0
              }
            >
              {save.isPending ? <Spinner className="h-4 w-4" /> : null}
              Kaydet
            </button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-4">
            <FormErrors error={save.error} />

            <Field label="Ad" htmlFor="wh-name" required>
              <input
                id="wh-name"
                className="field"
                value={editing.form.name}
                onChange={(event) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, name: event.target.value } })
                }
              />
            </Field>

            <Field
              label="Hedef adres"
              htmlFor="wh-url"
              required
              hint="Yalnızca https ve genel IP adresleri kabul edilir (SSRF koruması)."
            >
              <input
                id="wh-url"
                type="url"
                className="field font-mono text-sm"
                placeholder="https://ornek.com/webhook"
                value={editing.form.targetUrl}
                onChange={(event) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, targetUrl: event.target.value } })
                }
              />
            </Field>

            <fieldset>
              <legend className="label">Olaylar</legend>
              <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                {WEBHOOK_TOPICS.map((topic) => (
                  <label key={topic} className="flex items-center gap-2 text-xs text-ink-700">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 rounded border-ink-300 text-brand-600"
                      checked={editing.form.topics.includes(topic)}
                      onChange={(event) =>
                        setEditing(
                          (c) =>
                            c && {
                              ...c,
                              form: {
                                ...c.form,
                                topics: event.target.checked
                                  ? [...c.form.topics, topic]
                                  : c.form.topics.filter((item) => item !== topic),
                              },
                            },
                        )
                      }
                    />
                    <span className="font-mono">{topic}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <Field label="En fazla deneme" htmlFor="wh-retry">
              <input
                id="wh-retry"
                type="number"
                min={0}
                max={10}
                className="field"
                value={editing.form.maxRetryCount}
                onChange={(event) =>
                  setEditing(
                    (c) => c && { ...c, form: { ...c.form, maxRetryCount: Number(event.target.value) } },
                  )
                }
              />
            </Field>

            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-ink-300 text-brand-600"
                checked={editing.form.isActive}
                onChange={(event) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, isActive: event.target.checked } })
                }
              />
              Aktif
            </label>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={issuedSecret !== null}
        title="İmza anahtarı"
        description="Bu anahtar yalnızca bir kez gösterilir. Alıcı sistemde saklayın."
        onClose={() => setIssuedSecret(null)}
        footer={
          <button type="button" className="btn-primary" onClick={() => setIssuedSecret(null)}>
            Kapat
          </button>
        }
      >
        <p className="break-all rounded-lg bg-ink-900 px-4 py-3 font-mono text-sm text-white">
          {issuedSecret}
        </p>
        <p className="mt-3 text-sm text-ink-600">
          Gelen isteklerde <code className="font-mono">X-Webhook-Signature</code> başlığını bu
          anahtarla HMAC-SHA256 hesaplayarak doğrulayın.
        </p>
      </Modal>

      <Modal
        open={deliveriesFor !== null}
        title="Gönderim geçmişi"
        onClose={() => setDeliveriesFor(null)}
        size="xl"
      >
        {deliveries.isLoading ? (
          <p className="text-sm text-ink-500">Yükleniyor…</p>
        ) : (
          <div className="scroll-x">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                  <th className="pb-2 font-semibold">Olay</th>
                  <th className="pb-2 font-semibold">Durum</th>
                  <th className="pb-2 text-right font-semibold">HTTP</th>
                  <th className="pb-2 text-right font-semibold">Deneme</th>
                  <th className="pb-2 text-right font-semibold">Süre</th>
                  <th className="pb-2 font-semibold">Zaman</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {(deliveries.data?.items ?? []).map((delivery) => (
                  <tr key={delivery.id}>
                    <td className="py-2 font-mono text-xs text-ink-700">{delivery.eventType}</td>
                    <td className="py-2">
                      <span
                        className={`badge ${
                          delivery.status === 'Delivered'
                            ? 'bg-emerald-50 text-emerald-700'
                            : delivery.status === 'Failed' || delivery.status === 'DeadLettered'
                              ? 'bg-red-50 text-red-700'
                              : 'bg-amber-50 text-amber-800'
                        }`}
                      >
                        {delivery.status}
                      </span>
                    </td>
                    <td className="py-2 text-right text-ink-600">
                      {delivery.responseStatusCode ?? '—'}
                    </td>
                    <td className="py-2 text-right text-ink-600">{delivery.attemptCount}</td>
                    <td className="py-2 text-right text-ink-600">
                      {delivery.durationMs ? `${delivery.durationMs} ms` : '—'}
                    </td>
                    <td className="py-2 text-xs text-ink-500">
                      {formatDateTime(delivery.deliveredAt ?? delivery.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {(deliveries.data?.items ?? []).length === 0 ? (
              <p className="py-8 text-center text-sm text-ink-500">Gönderim kaydı yok.</p>
            ) : null}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={deleteId !== null}
        title="Webhook sil"
        message="Abonelik ve gönderim geçmişi silinir."
        confirmLabel="Sil"
        danger
        busy={remove.isPending}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId !== null && remove.mutate(deleteId)}
      />
    </div>
  );
}

/* ================================= GUNLUKLER ================================= */

const LOG_TABS = [
  { key: 'uygulama', label: 'Uygulama' },
  { key: 'denetim', label: 'Denetim kaydı' },
  { key: 'giris', label: 'Giriş denemeleri' },
] as const;

export function LogsPage() {
  const [tab, setTab] = useState<(typeof LOG_TABS)[number]['key']>('uygulama');

  return (
    <div>
      <PageHeader title="Günlükler" description="Uygulama hataları, denetim kayıtları ve giriş denemeleri" />

      <div className="mb-5 flex gap-1 overflow-x-auto border-b border-ink-200">
        {LOG_TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setTab(item.key)}
            className={`whitespace-nowrap border-b-2 px-4 py-2.5 text-sm transition ${
              tab === item.key
                ? 'border-ink-900 font-medium text-ink-900'
                : 'border-transparent text-ink-500 hover:text-ink-800'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'uygulama' ? <ApplicationLogsTab /> : null}
      {tab === 'denetim' ? <AuditLogsTab /> : null}
      {tab === 'giris' ? <LoginAttemptsTab /> : null}
    </div>
  );
}

function ApplicationLogsTab() {
  const [query, setQuery] = useState<LogQueryDto>({ page: 1, pageSize: 30 });
  const [detail, setDetail] = useState<string | null>(null);

  const logs = useQuery({
    queryKey: ['admin', 'logs', 'app', query],
    queryFn: () => adminLogsApi.application(query),
    placeholderData: keepPreviousData,
  });

  const columns: Column<NonNullable<typeof logs.data>['items'][number]>[] = [
    {
      key: 'time',
      header: 'Zaman',
      cell: (row) => <span className="text-ink-600">{formatDateTime(row.loggedAt)}</span>,
    },
    {
      key: 'level',
      header: 'Seviye',
      cell: (row) => (
        <span
          className={`badge ${
            row.level === 'Error' || row.level === 'Critical'
              ? 'bg-red-50 text-red-700'
              : row.level === 'Warning'
                ? 'bg-amber-50 text-amber-800'
                : 'bg-ink-100 text-ink-600'
          }`}
        >
          {row.level}
        </span>
      ),
    },
    {
      key: 'message',
      header: 'Mesaj',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-ink-800">{row.message}</p>
          {row.requestPath ? (
            <p className="truncate font-mono text-xs text-ink-400">
              {row.requestPath}
              {row.statusCode ? ` → ${row.statusCode}` : ''}
              {row.durationMs ? ` · ${row.durationMs} ms` : ''}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'user',
      header: 'Kullanıcı',
      hideOnMobile: true,
      cell: (row) => <span className="text-xs text-ink-500">{row.userName ?? 'Anonim'}</span>,
    },
    {
      key: 'trace',
      header: 'İz',
      hideOnMobile: true,
      cell: (row) => <span className="font-mono text-[10px] text-ink-400">{row.traceId ?? '—'}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (row) =>
        row.exception ? (
          <button type="button" className="btn-ghost btn-sm" onClick={() => setDetail(row.exception!)}>
            Ayrıntı
          </button>
        ) : null,
    },
  ];

  return (
    <div>
      <Toolbar>
        <SearchInput
          value={query.search ?? ''}
          onChange={(value) => setQuery((c) => ({ ...c, search: value || undefined, page: 1 }))}
          placeholder="Mesaj içinde ara"
        />

        <SelectFilter
          label="Seviye"
          value={query.level ?? ''}
          onChange={(value) =>
            setQuery((c) => ({ ...c, level: (value || undefined) as LogLevelType, page: 1 }))
          }
          options={logLevelTypeValues.map((value) => ({ value, label: value }))}
        />

        <div>
          <label className="label" htmlFor="log-trace">
            İz kimliği
          </label>
          <input
            id="log-trace"
            className="field w-44 py-2 font-mono text-sm"
            value={query.traceId ?? ''}
            onChange={(event) =>
              setQuery((c) => ({ ...c, traceId: event.target.value || undefined, page: 1 }))
            }
          />
        </div>
      </Toolbar>

      <DataTable
        columns={columns}
        rows={logs.data?.items ?? []}
        rowKey={(row) => row.id}
        isLoading={logs.isLoading}
        error={logs.error}
        onRetry={() => void logs.refetch()}
        emptyMessage="Günlük kaydı yok."
        footer={
          logs.data ? (
            <Pagination
              page={logs.data.page}
              totalPages={logs.data.totalPages}
              totalCount={logs.data.totalCount}
              onChange={(page) => setQuery((c) => ({ ...c, page }))}
            />
          ) : null
        }
      />

      <Modal
        open={detail !== null}
        title="Hata ayrıntısı"
        onClose={() => setDetail(null)}
        size="xl"
      >
        <pre className="scroll-x whitespace-pre-wrap rounded-lg bg-ink-950 p-4 font-mono text-xs text-ink-100">
          {detail}
        </pre>
      </Modal>
    </div>
  );
}

function AuditLogsTab() {
  const [query, setQuery] = useState<AuditLogQueryDto>({ page: 1, pageSize: 30 });

  const logs = useQuery({
    queryKey: ['admin', 'logs', 'audit', query],
    queryFn: () => adminLogsApi.audit(query),
    placeholderData: keepPreviousData,
  });

  const columns: Column<NonNullable<typeof logs.data>['items'][number]>[] = [
    {
      key: 'time',
      header: 'Zaman',
      cell: (row) => <span className="text-ink-600">{formatDateTime(row.createdAt)}</span>,
    },
    {
      key: 'action',
      header: 'İşlem',
      cell: (row) => <span className="badge bg-ink-100 text-ink-700">{row.action}</span>,
    },
    {
      key: 'entity',
      header: 'Kayıt',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-ink-800">
            {row.entityType}
            {row.entityLabel ? ` · ${row.entityLabel}` : ''}
          </p>
          {row.description ? (
            <p className="truncate text-xs text-ink-500">{truncate(row.description, 80)}</p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'fields',
      header: 'Değişen alanlar',
      hideOnMobile: true,
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.changedFields.slice(0, 4).map((field) => (
            <span key={field} className="badge bg-ink-50 font-mono text-[10px] text-ink-500">
              {field}
            </span>
          ))}
          {row.changedFields.length > 4 ? (
            <span className="text-xs text-ink-400">+{row.changedFields.length - 4}</span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'user',
      header: 'Kullanıcı',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-ink-800">{row.userName ?? 'Sistem'}</p>
          <p className="truncate text-xs text-ink-400">{row.ipAddress ?? ''}</p>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Toolbar>
        <SelectFilter
          label="İşlem"
          value={query.action ?? ''}
          onChange={(value) =>
            setQuery((c) => ({ ...c, action: (value || undefined) as AuditAction, page: 1 }))
          }
          options={auditActionValues.map((value) => ({ value, label: value }))}
        />

        <div>
          <label className="label" htmlFor="audit-entity">
            Kayıt türü
          </label>
          <input
            id="audit-entity"
            className="field w-40 py-2 text-sm"
            placeholder="Sipariş, Ürün..."
            value={query.entityType ?? ''}
            onChange={(event) =>
              setQuery((c) => ({ ...c, entityType: event.target.value || undefined, page: 1 }))
            }
          />
        </div>

        <div>
          <label className="label" htmlFor="audit-from">
            Başlangıç
          </label>
          <input
            id="audit-from"
            type="date"
            className="field py-2 text-sm"
            value={query.from?.slice(0, 10) ?? ''}
            onChange={(event) =>
              setQuery((c) => ({ ...c, from: event.target.value || undefined, page: 1 }))
            }
          />
        </div>
      </Toolbar>

      <DataTable
        columns={columns}
        rows={logs.data?.items ?? []}
        rowKey={(row) => row.id}
        isLoading={logs.isLoading}
        error={logs.error}
        onRetry={() => void logs.refetch()}
        emptyMessage="Denetim kaydı yok."
        footer={
          logs.data ? (
            <Pagination
              page={logs.data.page}
              totalPages={logs.data.totalPages}
              totalCount={logs.data.totalCount}
              onChange={(page) => setQuery((c) => ({ ...c, page }))}
            />
          ) : null
        }
      />
    </div>
  );
}

function LoginAttemptsTab() {
  const [query, setQuery] = useState<LoginAttemptQueryDto>({ page: 1, pageSize: 30 });

  const attempts = useQuery({
    queryKey: ['admin', 'logs', 'login', query],
    queryFn: () => adminLogsApi.loginAttempts(query),
    placeholderData: keepPreviousData,
  });

  const columns: Column<NonNullable<typeof attempts.data>['items'][number]>[] = [
    {
      key: 'time',
      header: 'Zaman',
      cell: (row) => <span className="text-ink-600">{formatDateTime(row.attemptedAt)}</span>,
    },
    {
      key: 'email',
      header: 'E-posta',
      cell: (row) => <span className="text-ink-800">{row.attemptedEmail}</span>,
    },
    {
      key: 'result',
      header: 'Sonuç',
      cell: (row) => (
        <span
          className={`badge ${
            row.result === 'Success'
              ? 'bg-emerald-50 text-emerald-700'
              : row.result === 'LockedOut'
                ? 'bg-red-50 text-red-700'
                : 'bg-amber-50 text-amber-800'
          }`}
        >
          {row.result}
        </span>
      ),
    },
    {
      key: 'ip',
      header: 'IP',
      cell: (row) => <span className="font-mono text-xs text-ink-600">{row.ipAddress ?? '—'}</span>,
    },
    {
      key: 'agent',
      header: 'Tarayıcı',
      hideOnMobile: true,
      cell: (row) => (
        <span className="text-xs text-ink-500">{truncate(row.userAgent ?? '—', 50)}</span>
      ),
    },
    {
      key: 'detail',
      header: 'Ayrıntı',
      hideOnMobile: true,
      cell: (row) => <span className="text-xs text-ink-500">{row.detail ?? '—'}</span>,
    },
  ];

  return (
    <div>
      <Toolbar>
        <div>
          <label className="label" htmlFor="la-email">
            E-posta
          </label>
          <input
            id="la-email"
            className="field w-52 py-2 text-sm"
            value={query.email ?? ''}
            onChange={(event) =>
              setQuery((c) => ({ ...c, email: event.target.value || undefined, page: 1 }))
            }
          />
        </div>

        <div>
          <label className="label" htmlFor="la-ip">
            IP adresi
          </label>
          <input
            id="la-ip"
            className="field w-40 py-2 font-mono text-sm"
            value={query.ipAddress ?? ''}
            onChange={(event) =>
              setQuery((c) => ({ ...c, ipAddress: event.target.value || undefined, page: 1 }))
            }
          />
        </div>

        <SelectFilter
          label="Sonuç"
          value={query.result ?? ''}
          onChange={(value) =>
            setQuery((c) => ({ ...c, result: (value || undefined) as LoginAttemptResult, page: 1 }))
          }
          options={loginAttemptResultValues.map((value) => ({ value, label: value }))}
        />

        <label className="mb-1 flex items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-ink-300 text-brand-600"
            checked={query.failedOnly === true}
            onChange={(event) =>
              setQuery((c) => ({ ...c, failedOnly: event.target.checked || undefined, page: 1 }))
            }
          />
          Yalnızca başarısızlar
        </label>
      </Toolbar>

      <DataTable
        columns={columns}
        rows={attempts.data?.items ?? []}
        rowKey={(row) => row.id}
        isLoading={attempts.isLoading}
        error={attempts.error}
        onRetry={() => void attempts.refetch()}
        emptyMessage="Giriş denemesi kaydı yok."
        footer={
          attempts.data ? (
            <Pagination
              page={attempts.data.page}
              totalPages={attempts.data.totalPages}
              totalCount={attempts.data.totalCount}
              onChange={(page) => setQuery((c) => ({ ...c, page }))}
            />
          ) : null
        }
      />
    </div>
  );
}

/* ================================== AYARLAR ================================== */

const SETTINGS_NAV = [
  { id: 'general', label: 'Genel Bilgiler', icon: Store },
  { id: 'reviews', label: 'Yorum ve Değerlendirme', icon: MessageSquare },
  { id: 'payment', label: 'Ödeme Yöntemleri', icon: CreditCard },
  { id: 'smtp', label: 'E-Posta & Bildirimler', icon: Mail },
  { id: 'social', label: 'Sosyal Medya', icon: Share2 },
  { id: 'analytics', label: 'Arama Motoru ve Takip', icon: Globe },
] as const;

type SettingsTab = (typeof SETTINGS_NAV)[number]['id'];

export function SettingsPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const [values, setValues] = useState<Record<string, string>>({});
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [copiedCallback, setCopiedCallback] = useState(false);

  const callbackUrl = typeof window !== 'undefined'
    ? `${window.location.protocol}//${window.location.hostname}${window.location.port ? (window.location.port === '5174' ? ':5001' : `:${window.location.port}`) : ''}/api/payments/paytr/callback`
    : '/api/payments/paytr/callback';

  const handleCopyCallback = () => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(callbackUrl);
      setCopiedCallback(true);
      toast.success('Bildirim URL adresi panoya kopyalandı.');
      setTimeout(() => setCopiedCallback(false), 2000);
    }
  };

  const settings = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => adminSettingsApi.getAll(),
  });

  useEffect(() => {
    if (settings.data) {
      setValues(
        Object.fromEntries(settings.data.map((setting) => [setting.key, setting.value ?? ''])),
      );
    }
  }, [settings.data]);

  const updateField = (key: string, val: string) => {
    setValues((prev) => ({ ...prev, [key]: val }));
  };

  const save = useMutation({
    mutationFn: () => adminSettingsApi.update({ settings: values }),
    onSuccess: (message) => {
      toast.success(message ?? 'Ayarlar başarıyla kaydedildi.');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
      void queryClient.invalidateQueries({ queryKey: ['public-settings'] });
    },
    onError: (err) => {
      toast.fromError(err, 'Ayarlar kaydedilemedi.');
    },
  });

  const testEmailMutation = useMutation({
    mutationFn: async (toEmail: string) => {
      await adminSettingsApi.update({ settings: values });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
      return adminSettingsApi.sendTestEmail(toEmail);
    },
    onSuccess: (message) => {
      toast.success(message ?? 'Test e-postası başarıyla iletildi.');
    },
    onError: (err) => {
      toast.fromError(err, 'Test e-postası gönderilemedi.');
    },
  });

  const sendAllAbandonedMutation = useMutation({
    mutationFn: () => adminDashboardApi.sendAllAbandonedCartEmails(),
    onSuccess: (data: any) => {
      toast.success(data?.message || 'Terk edilmiş sepet hatırlatmaları başarıyla gönderildi.');
    },
    onError: (err) => {
      toast.fromError(err, 'Toplu e-posta gönderilemedi.');
    },
  });

  if (settings.isLoading) return <PageLoader />;

  const isEditable = can(P.settings.manage);

  return (
    <div>
      <PageHeader
        title="Mağaza ve Sistem Ayarları"
        description="Mağaza kimliği, müşteri değerlendirme kuralları, ödeme kanalları, e-posta sunucusu ve takip kodları"
        actions={
          isEditable ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => save.mutate()}
              disabled={save.isPending}
            >
              {save.isPending ? <Spinner className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
              Değişiklikleri Kaydet
            </button>
          ) : null
        }
      />

      <div className="mb-4">
        <FormErrors error={save.error} />
      </div>

      {/* Sekme Menusu */}
      <div className="mb-6 flex gap-1 overflow-x-auto border-b border-ink-200">
        {SETTINGS_NAV.map((nav) => {
          const Icon = nav.icon;
          const isActive = activeTab === nav.id;
          return (
            <button
              key={nav.id}
              type="button"
              onClick={() => setActiveTab(nav.id)}
              className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition ${
                isActive
                  ? 'border-brand-600 text-brand-700 font-semibold'
                  : 'border-transparent text-ink-500 hover:border-ink-300 hover:text-ink-800'
              }`}
            >
              <Icon className={`h-4 w-4 ${isActive ? 'text-brand-600' : 'text-ink-400'}`} />
              {nav.label}
            </button>
          );
        })}
      </div>

      {/* 1. GENEL MAĞAZA BİLGİLERİ */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          <section className="card p-6">
            <h2 className="mb-1 text-base font-semibold text-ink-900">Mağaza Kimlik Bilgileri</h2>
            <p className="mb-5 text-xs text-ink-500">
              Vitrinde, faturalarda ve sistem bildirimlerinde görüntülenecek temel mağaza tanımları.
            </p>

            <div className="grid gap-5 md:grid-cols-2">
              <Field label="Mağaza Ticari Adı" htmlFor="set-store-name" required hint="Sitenin üst kısmında ve faturalarda yer alır">
                <input
                  id="set-store-name"
                  type="text"
                  disabled={!isEditable}
                  className="field"
                  value={values['store.name'] ?? ''}
                  onChange={(e) => updateField('store.name', e.target.value)}
                  placeholder="Örn: Butik E-Ticaret"
                />
              </Field>

              <Field label="Varsayılan Para Birimi" htmlFor="set-store-curr" hint="Sitedeki fiyat gösterimlerinde kullanılır">
                <select
                  id="set-store-curr"
                  disabled={!isEditable}
                  className="field"
                  value={values['store.default_currency'] ?? 'TRY'}
                  onChange={(e) => updateField('store.default_currency', e.target.value)}
                >
                  <option value="TRY">TRY (Türk Lirası - ₺)</option>
                  <option value="USD">USD (Amerikan Doları - $)</option>
                  <option value="EUR">EUR (Euro - €)</option>
                </select>
              </Field>

              <Field label="Destek ve İletişim E-Postası" htmlFor="set-store-email" hint="Müşterilerin size ulaşacağı e-posta adresi">
                <input
                  id="set-store-email"
                  type="email"
                  disabled={!isEditable}
                  className="field"
                  value={values['store.email'] ?? ''}
                  onChange={(e) => updateField('store.email', e.target.value)}
                  placeholder="destek@magazaniz.com"
                />
              </Field>

              <Field label="Müşteri Hizmetleri Telefonu" htmlFor="set-store-phone" hint="Sitenin iletişim alanında yayınlanır">
                <input
                  id="set-store-phone"
                  type="tel"
                  disabled={!isEditable}
                  className="field"
                  value={values['store.phone'] ?? ''}
                  onChange={(e) => updateField('store.phone', e.target.value)}
                  placeholder="0850 123 45 67"
                />
              </Field>

              <div className="md:col-span-2">
                <Field label="Şirket / Mağaza Açık Adresi" htmlFor="set-store-address" hint="İletişim sayfasında ve fatura altbilgisinde yer alır">
                  <input
                    id="set-store-address"
                    type="text"
                    disabled={!isEditable}
                    className="field"
                    value={values['store.address'] ?? ''}
                    onChange={(e) => updateField('store.address', e.target.value)}
                    placeholder="Merkez Mah. Ticaret Cad. No:1 Kadıköy / İstanbul"
                  />
                </Field>
              </div>

              <div className="md:col-span-2">
                <Field label="Mağaza Sloganı ve Kısa Tanıtım" htmlFor="set-store-desc" hint="Sayfa altbilgisinde (footer) ve arama motoru önizlemelerinde kullanılır">
                  <textarea
                    id="set-store-desc"
                    rows={3}
                    disabled={!isEditable}
                    className="field"
                    value={values['store.description'] ?? ''}
                    onChange={(e) => updateField('store.description', e.target.value)}
                    placeholder="Kaliteli ve güvenilir alışverişin adresi. En seçkin ürünler uygun fiyatlarla kapınızda."
                  />
                </Field>
              </div>
            </div>
          </section>

          <section className="card p-6">
            <h2 className="mb-1 text-base font-semibold text-ink-900">Görsel Kimlik (Logo ve Tarayıcı Simgesi)</h2>
            <p className="mb-5 text-xs text-ink-500">
              Mağazanızın üst menüsünde ve tarayıcı sekmelerinde görünecek logoları doğrudan bilgisayarınızdan yükleyiniz.
            </p>

            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-xl border border-ink-100 bg-ink-50/40 p-4">
                <ImageUploadField
                  label="Mağaza Logosu"
                  value={values['store.logo_url']}
                  onChange={(url) => updateField('store.logo_url', url)}
                  folder="settings"
                  hint="Sitenin üst menüsünde ve faturalarda görüntülenir. Şeffaf PNG veya SVG önerilir."
                  aspectRatioText="Önerilen boyut: 200x50 piksel"
                  disabled={!isEditable}
                />
              </div>

              <div className="rounded-xl border border-ink-100 bg-ink-50/40 p-4">
                <ImageUploadField
                  label="Tarayıcı Sekme Simgesi (Favicon)"
                  value={values['store.favicon_url']}
                  onChange={(url) => updateField('store.favicon_url', url)}
                  folder="settings"
                  hint="Ziyaretçilerin tarayıcı sekmelerinde ve yer imlerinde gösterilen küçük simge."
                  aspectRatioText="Önerilen boyut: 32x32 veya 64x64 piksel"
                  disabled={!isEditable}
                />
              </div>
            </div>
          </section>

          <section className="card p-6">
            <div className="flex items-center gap-2 mb-1">
              <Megaphone className="h-5 w-5 text-brand-600" />
              <h2 className="text-base font-semibold text-ink-900">Duyuru Bandı (Üst Kayan Metin Şeridi)</h2>
            </div>
            <p className="mb-5 text-xs text-ink-500">
              Sitenin en üst kısmında ziyaretçileri karşılayan kayan duyuru ve kampanya şeridinin ayarları.
            </p>

            <div className="space-y-5">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  disabled={!isEditable}
                  className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                  checked={values['store.announcement_enabled'] !== 'false'}
                  onChange={(e) =>
                    updateField('store.announcement_enabled', e.target.checked ? 'true' : 'false')
                  }
                />
                <span className="text-sm font-medium text-ink-800">
                  Duyuru bandını vitrinde aktif et ve göster
                </span>
              </label>

              {values['store.announcement_enabled'] !== 'false' && (
                <>
                  <Field
                    label="Duyuru Metinleri (Her satıra bir duyuru)"
                    htmlFor="set-announcement-texts"
                    hint="Birden fazla duyuru göstermek isterseniz her birini alt alta yeni bir satıra yazın. Şerit üzerinde sırayla ve sürekli kayacaktır. Fiyat veya kampanya bilgisini dilediğiniz gibi metin içine doğrudan yazabilirsiniz."
                  >
                    <textarea
                      id="set-announcement-texts"
                      rows={4}
                      disabled={!isEditable}
                      className="field font-sans"
                      value={
                        values['store.announcement_texts'] !== undefined
                          ? values['store.announcement_texts']
                          : '🌙 Kutsal Topraklardan Sofranıza Taze Medine Hurmaları ve Hac Malzemeleri\n₺750 ve üzeri siparişlerde kargo ücretsiz!'
                      }
                      onChange={(e) => updateField('store.announcement_texts', e.target.value)}
                      placeholder="🌙 Kutsal Topraklardan Sofranıza Taze Medine Hurmaları ve Hac Malzemeleri&#10;₺750 ve üzeri siparişlerde kargo ücretsiz!&#10;Hızlı ve Güvenilir Teslimat"
                    />
                  </Field>

                  {/* Canlı Önizleme */}
                  <div>
                    <span className="text-xs font-semibold text-ink-500 uppercase tracking-wider block mb-1.5">
                      Canlı Önizleme (Vitrinde Görünüm)
                    </span>
                    <div className="overflow-hidden rounded-lg border border-brand-800/20 bg-gradient-to-r from-brand-900 via-brand-800 to-brand-900 py-2.5 text-xs font-medium text-brand-100 shadow-inner">
                      <div className="flex items-center gap-8 whitespace-nowrap px-4 overflow-x-auto">
                        {(
                          (values['store.announcement_texts'] !== undefined
                            ? values['store.announcement_texts']
                            : '🌙 Kutsal Topraklardan Sofranıza Taze Medine Hurmaları ve Hac Malzemeleri\n₺750 ve üzeri siparişlerde kargo ücretsiz!'
                          )
                            .split('\n')
                            .map((s) => s.trim())
                            .filter(Boolean)
                        ).map((text, idx) => (
                          <span key={idx} className="flex items-center gap-2 shrink-0">
                            <span>{text}</span>
                            <span className="text-amber-400/80">✦</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </section>

          <section className="card p-6">
            <div className="flex items-center gap-2 mb-1">
              <Truck className="h-5 w-5 text-brand-600" />
              <h2 className="text-base font-semibold text-ink-900">Kargo & Sepet Limitleri</h2>
            </div>
            <p className="mb-5 text-xs text-ink-500">
              Sepet hesaplamalarında ve vitrinde geçerli olan ücretsiz kargo eşik ayarı.
            </p>

            <div className="max-w-md">
              <Field
                label="Ücretsiz Kargo Sepet Tutarı (₺)"
                htmlFor="set-shipping-threshold"
                hint="Müşterinin sepet tutarı bu değere ulaştığında kargo ücretsiz sayılır. Sepet ekranındaki 'Ücretsiz kargoya X TL kaldı' hesabı bu değere göre yapılır."
              >
                <input
                  id="set-shipping-threshold"
                  type="number"
                  min="0"
                  step="1"
                  disabled={!isEditable}
                  className="field font-semibold"
                  value={values['checkout.free_shipping_threshold'] ?? '750'}
                  onChange={(e) => updateField('checkout.free_shipping_threshold', e.target.value)}
                  placeholder="750"
                />
              </Field>
            </div>
          </section>

          {/* E-Bülten & Hoş Geldin Kuponu Ayarları */}
          <section className="card p-6">
            <div className="flex items-center gap-2 mb-1">
              <Tag className="h-5 w-5 text-brand-600" />
              <h2 className="text-base font-semibold text-ink-900">E-Bülten & Hoş Geldin İndirim Kuponu</h2>
            </div>
            <p className="mb-5 text-xs text-ink-500">
              Ana sayfadaki 'İlk Siparişine Özel İndirim Kazan' bölümünde ve bülten abonelerine tanımlanan kuponun ayarları.
            </p>

            <div className="grid gap-6 sm:grid-cols-3">
              <Field
                label="İndirim Tutarı (₺)"
                htmlFor="set-newsletter-discount"
                hint="Ana sayfadaki bülten kutusunda ve kuponda geçerli indirim tutarı (örn: 100)."
              >
                <input
                  id="set-newsletter-discount"
                  type="number"
                  min="0"
                  step="1"
                  disabled={!isEditable}
                  className="field font-semibold"
                  value={values['store.newsletter_discount_amount'] || '100'}
                  onChange={(e) => updateField('store.newsletter_discount_amount', e.target.value)}
                  placeholder="100"
                />
              </Field>

              <Field
                label="Kupon Kodu"
                htmlFor="set-newsletter-code"
                hint="Müşteriye e-posta ile iletilen ve sepette uygulanacak kupon kodu."
              >
                <input
                  id="set-newsletter-code"
                  type="text"
                  disabled={!isEditable}
                  className="field font-mono uppercase font-bold"
                  value={values['store.newsletter_coupon_code'] || 'BULTEN100'}
                  onChange={(e) => updateField('store.newsletter_coupon_code', e.target.value.toUpperCase())}
                  placeholder="BULTEN100"
                />
              </Field>

              <Field
                label="Asgari Sepet Tutarı (₺, İsteğe Bağlı)"
                htmlFor="set-newsletter-min-subtotal"
                hint="Kuponun geçerli olması için gereken alt sepet limiti. Boş bırakılırsa limitsizdir."
              >
                <input
                  id="set-newsletter-min-subtotal"
                  type="number"
                  min="0"
                  step="1"
                  disabled={!isEditable}
                  className="field"
                  value={values['store.newsletter_min_subtotal'] || ''}
                  onChange={(e) => updateField('store.newsletter_min_subtotal', e.target.value)}
                  placeholder="Limitsiz (boş)"
                />
              </Field>
            </div>
          </section>
        </div>
      )}

      {/* 2. YORUM VE DEĞERLENDİRME AYARLARI */}
      {activeTab === 'reviews' && (
        <div className="space-y-6">
          <section className="card p-6">
            <h2 className="mb-1 text-base font-semibold text-ink-900">Müşteri Yorum ve Değerlendirme Kuralları</h2>
            <p className="mb-5 text-xs text-ink-500">
              Sitenizdeki ürünlere müşterilerin yazacağı yorumların ve puanların onaylanma ve izin kuralları.
            </p>

            <div className="space-y-4">
              {/* Satın alma şartı */}
              <div className="rounded-xl border border-ink-200 bg-white p-5 transition hover:border-ink-300">
                <label className="flex items-start gap-4 cursor-pointer">
                  <input
                    type="checkbox"
                    disabled={!isEditable}
                    className="mt-1 h-5 w-5 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                    checked={values['catalog.require_purchase_for_review'] === 'true'}
                    onChange={(e) => updateField('catalog.require_purchase_for_review', String(e.target.checked))}
                  />
                  <div className="space-y-1">
                    <span className="text-sm font-semibold text-ink-900">
                      Yorum Yapabilmek İçin Ürünün Satın Alınmış Olması Zorunlu Olsun
                    </span>
                    <p className="text-xs text-ink-500">
                      Bu seçenek işaretlendiğinde yalnızca siparişi teslim edilmiş gerçek alıcılar ürün değerlendirmesi bırakabilir.
                    </p>
                    <div className="pt-1">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        values['catalog.require_purchase_for_review'] === 'true'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-amber-50 text-amber-700'
                      }`}>
                        {values['catalog.require_purchase_for_review'] === 'true'
                          ? 'Şu anki durum: Yalnızca ürünü satın almış ve teslim edilmiş müşteriler yorum yapabilir.'
                          : 'Şu anki durum: Tüm kayıtlı üyeler satın almamış olsalar bile ürüne yorum yazabilir.'}
                      </span>
                    </div>
                  </div>
                </label>
              </div>

              {/* Yönetici onayı şartı */}
              <div className="rounded-xl border border-ink-200 bg-white p-5 transition hover:border-ink-300">
                <label className="flex items-start gap-4 cursor-pointer">
                  <input
                    type="checkbox"
                    disabled={!isEditable}
                    className="mt-1 h-5 w-5 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                    checked={values['catalog.moderate_reviews'] === 'true'}
                    onChange={(e) => updateField('catalog.moderate_reviews', String(e.target.checked))}
                  />
                  <div className="space-y-1">
                    <span className="text-sm font-semibold text-ink-900">
                      Yeni Eklenen Yorumlar Yönetici Onayından Geçsin
                    </span>
                    <p className="text-xs text-ink-500">
                      Bu seçenek işaretlendiğinde yapılan yeni yorumlar 'Onay Bekliyor' durumuna alınır ve siz yönetim panelinden onaylamadan ürün sayfasında yayınlanmaz.
                    </p>
                    <div className="pt-1">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        values['catalog.moderate_reviews'] === 'true'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-blue-50 text-blue-700'
                      }`}>
                        {values['catalog.moderate_reviews'] === 'true'
                          ? 'Şu anki durum: Yorumlar admin panelden incelenip onaylandıktan sonra vitrinde yayına girer.'
                          : 'Şu anki durum: Yapılan yorumlar anında otomatik olarak sitede yayına girer.'}
                      </span>
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* 3. ÖDEME YÖNTEMLERİ */}
      {activeTab === 'payment' && (
        <div className="space-y-6">
          {/* KREDİ KARTI / SANAL POS */}
          <section className="card p-6">
            <div className="flex items-center justify-between pb-4 border-b border-ink-100">
              <div>
                <h2 className="text-base font-semibold text-ink-900">Kredi Kartı / Sanal POS Entegrasyonu</h2>
                <p className="text-xs text-ink-500">Müşterilerin sepet tamamlama sırasında kartla ödeme yapmasını sağlar.</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  disabled={!isEditable}
                  className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                  checked={values['payment.credit_card_enabled'] === 'true'}
                  onChange={(e) => updateField('payment.credit_card_enabled', String(e.target.checked))}
                />
                <span className="text-sm font-medium text-ink-800">
                  {values['payment.credit_card_enabled'] === 'true' ? 'Yöntem Aktif' : 'Yöntem Pasif'}
                </span>
              </label>
            </div>

            {values['payment.credit_card_enabled'] === 'true' && (
              <div className="mt-5 space-y-6">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Sanal POS Sağlayıcısı" htmlFor="set-pos-prov" hint="Kullanmak istediğiniz ödeme aracısını seçiniz">
                    <select
                      id="set-pos-prov"
                      disabled={!isEditable}
                      className="field"
                      value={values['payment.credit_card_provider'] ?? 'paytr'}
                      onChange={(e) => updateField('payment.credit_card_provider', e.target.value)}
                    >
                      <option value="paytr">PayTR Direkt API & 3D Secure — Önerilen</option>
                      <option value="test">Test / Simülasyon POS (Geliştirme Amaçlı)</option>
                      <option value="iyzico">İyzico Sanal POS</option>
                    </select>
                  </Field>

                  <div className="flex items-center pt-6">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        disabled={!isEditable}
                        className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                        checked={
                          (values['payment.credit_card_provider'] === 'paytr'
                            ? (values['payment.paytr_test_mode'] ?? values['payment.credit_card_test_mode'])
                            : values['payment.credit_card_test_mode']) === 'true'
                        }
                        onChange={(e) => {
                          const val = String(e.target.checked);
                          updateField('payment.paytr_test_mode', val);
                          updateField('payment.credit_card_test_mode', val);
                        }}
                      />
                      <div>
                        <span className="text-sm font-semibold text-ink-900">Test Modu Etkin</span>
                        <p className="text-xs text-ink-400">
                          {values['payment.credit_card_provider'] === 'test'
                            ? 'Açık olduğunda işlem otomatik başarılı simüle edilir.'
                            : 'Açık olduğunda PayTR test kartları ile gerçek çekim olmadan test edebilirsiniz.'}
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* PAYTR ENTEGRASYONU */}
                {(values['payment.credit_card_provider'] ?? 'paytr') === 'paytr' && (
                  <div className="space-y-6 pt-2">
                    <div className="rounded-lg border border-brand-200 bg-brand-50/60 p-4">
                      <div className="flex items-start gap-3">
                        <ShieldCheck className="h-5 w-5 text-brand-700 mt-0.5 shrink-0" />
                        <div className="space-y-1 text-sm">
                          <p className="font-semibold text-brand-900">PayTR Direkt API & 3D Secure Sanal POS Entegrasyonu</p>
                          <p className="text-brand-700 text-xs">
                            Müşterileriniz ödeme sayfasında doğrudan kart bilgilerini ve taksit seçeneğini girer; onay adımında PayTR 3D Secure geçidine aktarılarak güvenle ödeme yapar. Kart bilgileri sunucuda saklanmaz.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field
                        label="Mağaza Numarası (Merchant ID)"
                        htmlFor="set-paytr-mid"
                        required
                        hint="PayTR Mağaza panelinizdeki BİLGİ sayfasında bulunan Mağaza No"
                      >
                        <input
                          id="set-paytr-mid"
                          type="text"
                          disabled={!isEditable}
                          className="field font-mono"
                          value={values['payment.paytr_merchant_id'] ?? values['payment.credit_card_merchant_id'] ?? ''}
                          onChange={(e) => {
                            updateField('payment.paytr_merchant_id', e.target.value);
                            updateField('payment.credit_card_merchant_id', e.target.value);
                          }}
                          placeholder="Örn: 123456"
                        />
                      </Field>

                      <Field
                        label="Mağaza Parolası (Merchant Key)"
                        htmlFor="set-paytr-key"
                        required
                        hint="PayTR BİLGİ sayfasındaki Mağaza Parolası"
                      >
                        <input
                          id="set-paytr-key"
                          type="text"
                          disabled={!isEditable}
                          className="field font-mono"
                          value={values['payment.paytr_merchant_key'] ?? values['payment.credit_card_api_key'] ?? ''}
                          onChange={(e) => {
                            updateField('payment.paytr_merchant_key', e.target.value);
                            updateField('payment.credit_card_api_key', e.target.value);
                          }}
                          placeholder="Örn: gYk7..."
                        />
                      </Field>

                      <div className="sm:col-span-2">
                        <Field
                          label="Mağaza Gizli Anahtarı (Merchant Salt)"
                          htmlFor="set-paytr-salt"
                          required
                          hint="PayTR BİLGİ sayfasındaki Mağaza Gizli Anahtarı"
                        >
                          <input
                            id="set-paytr-salt"
                            type="password"
                            disabled={!isEditable}
                            className="field font-mono"
                            value={values['payment.paytr_merchant_salt'] ?? values['payment.credit_card_secret_key'] ?? ''}
                            onChange={(e) => {
                              updateField('payment.paytr_merchant_salt', e.target.value);
                              updateField('payment.credit_card_secret_key', e.target.value);
                            }}
                            placeholder="••••••••••••••••••••••••"
                          />
                        </Field>
                      </div>

                      <Field label="Taksit Seçeneği" htmlFor="set-paytr-no-inst" hint="Müşterilere taksitli ödeme imkanı verilsin mi?">
                        <select
                          id="set-paytr-no-inst"
                          disabled={!isEditable}
                          className="field font-medium"
                          value={values['payment.paytr_no_installment'] ?? '0'}
                          onChange={(e) => updateField('payment.paytr_no_installment', e.target.value)}
                        >
                          <option value="0">Taksitli ve Tek Çekim (Tüm Seçenekler Aktif)</option>
                          <option value="1">Yalnızca Tek Çekim (Taksit Seçeneği Kapatılsın)</option>
                        </select>
                      </Field>

                      {values['payment.paytr_no_installment'] === '1' ? (
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-3 text-xs text-emerald-800 flex items-center gap-2">
                          <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                          <span>Yalnızca Tek Çekim seçili: Ödeme sayfasında taksit tablosu gizlenir, tüm kartlardan yalnızca peşin çekim yapılır.</span>
                        </div>
                      ) : (
                        <Field label="Maksimum Taksit Sayısı" htmlFor="set-paytr-max-inst" hint="İzin verilen azami taksit adedi">
                          <select
                            id="set-paytr-max-inst"
                            disabled={!isEditable}
                            className="field"
                            value={values['payment.paytr_max_installment'] ?? '0'}
                            onChange={(e) => updateField('payment.paytr_max_installment', e.target.value)}
                          >
                            <option value="0">Tüm Taksitler (12 Taksite kadar)</option>
                            <option value="2">Maksimum 2 Taksit</option>
                            <option value="3">Maksimum 3 Taksit</option>
                            <option value="6">Maksimum 6 Taksit</option>
                            <option value="9">Maksimum 9 Taksit</option>
                            <option value="12">Maksimum 12 Taksit</option>
                          </select>
                        </Field>
                      )}

                      <Field label="Ödeme Sayfası Zaman Aşımı (Dakika)" htmlFor="set-paytr-timeout" hint="Müşterinin ödemeyi tamamlaması için tanınan süre">
                        <input
                          id="set-paytr-timeout"
                          type="number"
                          min="5"
                          max="120"
                          disabled={!isEditable}
                          className="field"
                          value={values['payment.paytr_timeout_limit'] ?? '30'}
                          onChange={(e) => updateField('payment.paytr_timeout_limit', e.target.value)}
                          placeholder="30"
                        />
                      </Field>

                      <Field label="Geliştirme Test IP Adresi" htmlFor="set-paytr-ip" hint="PayTR localhost IP'lerini reddettiği için yerel ortamda bu IP kullanılır">
                        <input
                          id="set-paytr-ip"
                          type="text"
                          disabled={!isEditable}
                          className="field font-mono"
                          value={values['payment.paytr_test_ip'] ?? '85.105.1.1'}
                          onChange={(e) => updateField('payment.paytr_test_ip', e.target.value)}
                          placeholder="85.105.1.1"
                        />
                      </Field>
                    </div>

                    {/* PayTR Bildirim URL (Callback URL) Kartı */}
                    <div className="rounded-xl border border-brand-200 bg-brand-50/40 p-5 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="flex h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
                          <h3 className="text-sm font-semibold text-ink-900">
                            PayTR 2. Adım — Bildirim URL (Callback / Webhook URL)
                          </h3>
                        </div>
                        <a
                          href="https://www.paytr.com/magaza/ayarlar"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-medium text-brand-600 hover:text-brand-800 flex items-center gap-1"
                        >
                          PayTR Mağaza Paneli <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>

                      <p className="text-xs text-ink-600 leading-relaxed">
                        PayTR mağaza panelinize (paytr.com) giriş yaparak <strong>Ayarlar ➔ Bildirim URL Ayarları (Callback URL)</strong> sekmesine gidiniz ve aşağıdaki bağlantıyı ilgili alana kaydediniz. PayTR, başarılı ve başarısız ödeme bildirimlerini bu adrese ileterek siparişleri otomatik onaylar.
                      </p>

                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          readOnly
                          className="field font-mono text-xs bg-white text-ink-700 select-all cursor-pointer"
                          value={callbackUrl}
                          onClick={(e) => (e.target as HTMLInputElement).select()}
                        />
                        <button
                          type="button"
                          onClick={handleCopyCallback}
                          className="btn-secondary whitespace-nowrap shrink-0 flex items-center gap-1.5"
                        >
                          {copiedCallback ? (
                            <>
                              <Check className="h-4 w-4 text-emerald-600" />
                              <span className="text-emerald-700">Kopyalandı</span>
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4" />
                              <span>URL'yi Kopyala</span>
                            </>
                          )}
                        </button>
                      </div>

                      <p className="text-[11px] text-brand-800 bg-brand-100/60 rounded px-2.5 py-1.5 border border-brand-200/50">
                        💡 <strong>Canlıya Geçiş Notu:</strong> Şu anda yerel ortamda çalıştığınız için adres <code>http://localhost:5001...</code> olarak görünmektedir. Sitenizi canlı alan adınıza yüklediğinizde bu adres otomatik olarak kendi alan adınızla (örneğin <code>https://siteniz.com/api/payments/paytr/callback</code>) güncellenecektir ve PayTR mağaza panelinize de sitenizin gerçek alan adıyla girilmelidir.
                      </p>
                    </div>
                  </div>
                )}

                {/* İYZİCO ENTEGRASYONU */}
                {values['payment.credit_card_provider'] === 'iyzico' && (
                  <div className="grid gap-5 sm:grid-cols-2 pt-2">
                    <Field label="İyzico API Anahtarı" htmlFor="set-iyz-key" hint="İyzico panelinizden alacağınız API Key">
                      <input
                        id="set-iyz-key"
                        type="text"
                        disabled={!isEditable}
                        className="field font-mono"
                        value={values['payment.credit_card_api_key'] ?? ''}
                        onChange={(e) => updateField('payment.credit_card_api_key', e.target.value)}
                        placeholder="sandbox-apk_..."
                      />
                    </Field>
                    <Field label="İyzico Güvenlik Anahtarı" htmlFor="set-iyz-secret" hint="İyzico Secret Key">
                      <input
                        id="set-iyz-secret"
                        type="password"
                        disabled={!isEditable}
                        className="field font-mono"
                        value={values['payment.credit_card_secret_key'] ?? ''}
                        onChange={(e) => updateField('payment.credit_card_secret_key', e.target.value)}
                        placeholder="••••••••••••••••••••••••"
                      />
                    </Field>
                  </div>
                )}

                {/* TEST MODU AÇIKLAMASI */}
                {values['payment.credit_card_provider'] === 'test' && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800">
                    Test modunda herhangi bir ödeme sağlayıcısı veya gerçek kart bilgisi gerekmez. Müşteri sepette "Siparişi Tamamla" butonuna bastığı an işlem doğrudan simülasyon olarak onaylanır.
                  </div>
                )}
              </div>
            )}
          </section>

          <section className="card p-6">
            <div className="flex items-center justify-between pb-4 border-b border-ink-100">
              <div>
                <h2 className="text-base font-semibold text-ink-900">Banka Havalesi / EFT Entegrasyonu</h2>
                <p className="text-xs text-ink-500">Müşterilerin banka hesabınıza para transferi yaparak sipariş vermesini sağlar.</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  disabled={!isEditable}
                  className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                  checked={values['payment.bank_transfer_enabled'] === 'true'}
                  onChange={(e) => updateField('payment.bank_transfer_enabled', String(e.target.checked))}
                />
                <span className="text-sm font-medium text-ink-800">
                  {values['payment.bank_transfer_enabled'] === 'true' ? 'Yöntem Aktif' : 'Yöntem Pasif'}
                </span>
              </label>
            </div>

            {values['payment.bank_transfer_enabled'] === 'true' && (
              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <Field label="Banka Adı" htmlFor="set-bank-name" required hint="Örn: Ziraat Bankası, Türkiye İş Bankası, Garanti BBVA">
                  <input
                    id="set-bank-name"
                    type="text"
                    disabled={!isEditable}
                    className="field"
                    value={values['payment.bank_transfer_bank_name'] ?? ''}
                    onChange={(e) => updateField('payment.bank_transfer_bank_name', e.target.value)}
                    placeholder="Ziraat Bankası"
                  />
                </Field>

                <Field label="Hesap Sahibi (Ad Soyad veya Şirket Ünvanı)" htmlFor="set-bank-holder" required hint="Havale açıklamasına yazılacak alıcı adı">
                  <input
                    id="set-bank-holder"
                    type="text"
                    disabled={!isEditable}
                    className="field"
                    value={values['payment.bank_transfer_account_holder'] ?? ''}
                    onChange={(e) => updateField('payment.bank_transfer_account_holder', e.target.value)}
                    placeholder="Örn: Butik Mağazacılık Ltd. Şti."
                  />
                </Field>

                <div className="sm:col-span-2">
                  <Field label="IBAN Numarası" htmlFor="set-bank-iban" required hint="Ödeme sayfasında müşteriye gösterilecek banka hesabı">
                    <input
                      id="set-bank-iban"
                      type="text"
                      disabled={!isEditable}
                      className="field font-mono"
                      value={values['payment.bank_transfer_iban'] ?? ''}
                      onChange={(e) => updateField('payment.bank_transfer_iban', e.target.value)}
                      placeholder="TR00 0000 0000 0000 0000 0000 00"
                    />
                  </Field>
                </div>

                <div className="sm:col-span-2">
                  <Field label="Ödeme ve Sipariş Açıklama Notu" htmlFor="set-bank-desc" hint="Müşteriye ödeme ekranında gösterilecek bilgilendirme notu">
                    <textarea
                      id="set-bank-desc"
                      rows={2}
                      disabled={!isEditable}
                      className="field"
                      value={values['payment.bank_transfer_description'] ?? ''}
                      onChange={(e) => updateField('payment.bank_transfer_description', e.target.value)}
                      placeholder="Lütfen havale/EFT açıklamasına sipariş numaranızı yazınız. Ödemeniz onaylandıktan sonra siparişiniz kargoya verilecektir."
                    />
                  </Field>
                </div>
              </div>
            )}
          </section>

          <section className="card p-6">
            <div className="flex items-center justify-between pb-4 border-b border-ink-100">
              <div>
                <h2 className="text-base font-semibold text-ink-900">Kapıda Nakit / Kartla Ödeme</h2>
                <p className="text-xs text-ink-500">Sipariş bedelinin ürün teslimatı sırasında kargo kuryesine ödenmesi.</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  disabled={!isEditable}
                  className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                  checked={values['payment.cod_enabled'] === 'true'}
                  onChange={(e) => updateField('payment.cod_enabled', String(e.target.checked))}
                />
                <span className="text-sm font-medium text-ink-800">
                  {values['payment.cod_enabled'] === 'true' ? 'Yöntem Aktif' : 'Yöntem Pasif'}
                </span>
              </label>
            </div>

            {values['payment.cod_enabled'] === 'true' && (
              <div className="mt-5 max-w-sm">
                <Field label="Kapıda Ödeme Ek Hizmet Bedeli (₺)" htmlFor="set-cod-fee" hint="Kargo firmasının tahsilat hizmeti için faturaya eklenecek ek tutar (0 = ücretsiz)">
                  <input
                    id="set-cod-fee"
                    type="number"
                    step="0.5"
                    min="0"
                    disabled={!isEditable}
                    className="field"
                    value={values['payment.cod_fee'] ?? '0'}
                    onChange={(e) => updateField('payment.cod_fee', e.target.value)}
                    placeholder="25.00"
                  />
                </Field>
              </div>
            )}
          </section>
        </div>
      )}

      {/* 4. E-POSTA (SMTP) SUNUCUSU */}
      {activeTab === 'smtp' && (
        <div className="space-y-6">
          <section className="card p-6">
            <h2 className="mb-1 text-base font-semibold text-ink-900">E-Posta (SMTP) Sunucu Yapılandırması</h2>
            <p className="mb-5 text-xs text-ink-500">
              Sipariş bildirimleri, kargo takipleri ve şifre sıfırlama e-postalarının gönderileceği sunucu bağlantı parametreleri.
            </p>

            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="SMTP Sunucu Adresi" htmlFor="set-smtp-host" required hint="Örn: smtp.gmail.com veya mail.alanadiniz.com">
                <input
                  id="set-smtp-host"
                  type="text"
                  disabled={!isEditable}
                  className="field"
                  value={values['smtp.host'] ?? ''}
                  onChange={(e) => updateField('smtp.host', e.target.value)}
                  placeholder="smtp.gmail.com"
                />
              </Field>

              <Field label="Sunucu Bağlantı Noktası (Port)" htmlFor="set-smtp-port" required hint="Genellikle 587 (TLS bağlantısı) veya 465 (SSL bağlantısı)">
                <input
                  id="set-smtp-port"
                  type="number"
                  disabled={!isEditable}
                  className="field"
                  value={values['smtp.port'] ?? '587'}
                  onChange={(e) => updateField('smtp.port', e.target.value)}
                  placeholder="587"
                />
              </Field>

              <Field label="Kullanıcı Adı" htmlFor="set-smtp-user" required hint="E-posta sunucunuzun oturum açma kullanıcı adı veya e-posta adresi">
                <input
                  id="set-smtp-user"
                  type="text"
                  disabled={!isEditable}
                  className="field"
                  value={values['smtp.username'] ?? ''}
                  onChange={(e) => updateField('smtp.username', e.target.value)}
                  placeholder="bildirim@magazaniz.com"
                />
              </Field>

              <Field label="Parola / Uygulama Şifresi" htmlFor="set-smtp-pass" required hint="E-posta hesabınızın parolası veya Gmail/Outlook uygulama anahtarı">
                <input
                  id="set-smtp-pass"
                  type="password"
                  disabled={!isEditable}
                  className="field"
                  value={values['smtp.password'] ?? ''}
                  onChange={(e) => updateField('smtp.password', e.target.value)}
                  placeholder="••••••••••••"
                />
              </Field>

              <Field label="Gönderen E-Posta Adresi" htmlFor="set-smtp-from" required hint="Müşterilere gidecek e-postaların 'Kimden' hanesinde görünecek adres">
                <input
                  id="set-smtp-from"
                  type="email"
                  disabled={!isEditable}
                  className="field"
                  value={values['smtp.from_address'] ?? ''}
                  onChange={(e) => updateField('smtp.from_address', e.target.value)}
                  placeholder="siparis@magazaniz.com"
                />
              </Field>

              <Field label="Gönderici Görünen Başlığı" htmlFor="set-smtp-name" hint="E-posta gelen kutusunda mağazanızın ismi olarak görünür">
                <input
                  id="set-smtp-name"
                  type="text"
                  disabled={!isEditable}
                  className="field"
                  value={values['smtp.from_display_name'] ?? ''}
                  onChange={(e) => updateField('smtp.from_display_name', e.target.value)}
                  placeholder="Örn: Butik Mağaza Bildirim"
                />
              </Field>

              <div className="sm:col-span-2 pt-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    disabled={!isEditable}
                    className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                    checked={values['smtp.enable_ssl'] === 'true'}
                    onChange={(e) => updateField('smtp.enable_ssl', String(e.target.checked))}
                  />
                  <div>
                    <span className="text-sm font-medium text-ink-900">Güvenli Bağlantı (SSL / TLS) Şifrelemesi Etkin</span>
                    <p className="text-xs text-ink-400">Tüm modern e-posta sunucuları için önerilir.</p>
                  </div>
                </label>
              </div>
            </div>
          </section>

          {/* TEST E-POSTASI GÖNDERME */}
          <section className="card p-6 border-l-4 border-l-brand-600">
            <h2 className="mb-1 text-base font-semibold text-ink-900">Bağlantıyı Sına ve Test E-postası Gönder</h2>
            <p className="mb-4 text-xs text-ink-500">
              Yapılandırdığınız SMTP sunucu ayarlarının doğru çalıştığını canlı denemek için bir alıcı adresi girerek test e-postası gönderiniz.
              (Değişiklik yaptıysanız önce üstteki 'Değişiklikleri Kaydet' butonuna basınız).
            </p>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-end max-w-xl">
              <div className="flex-1">
                <Field label="Test Alıcısı E-Posta Adresi" htmlFor="set-test-email">
                  <input
                    id="set-test-email"
                    type="email"
                    className="field"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    placeholder="ornek@alanadiniz.com"
                  />
                </Field>
              </div>
              <button
                type="button"
                className="btn-secondary h-10 flex items-center justify-center gap-2 whitespace-nowrap"
                disabled={testEmailMutation.isPending || !testEmailAddress.trim()}
                onClick={() => {
                  if (!testEmailAddress.trim()) {
                    toast.error('Lütfen geçerli bir test e-posta adresi yazınız.');
                    return;
                  }
                  testEmailMutation.mutate(testEmailAddress.trim());
                }}
              >
                {testEmailMutation.isPending ? (
                  <Spinner className="h-4 w-4" />
                ) : (
                  <Send className="h-4 w-4 text-brand-600" />
                )}
                Sına ve Gönder
              </button>
            </div>
          </section>

          {/* YENİ SİPARİŞ YÖNETİCİ BİLDİRİM E-POSTALARI */}
          <section className="card p-6 border-l-4 border-l-amber-500">
            <div className="flex items-center gap-2 mb-1">
              <Bell className="h-5 w-5 text-amber-600" />
              <h2 className="text-base font-semibold text-ink-900">
                Yeni Sipariş Yönetici Bildirim E-Postaları
              </h2>
            </div>
            <p className="mb-5 text-xs text-ink-500">
              Mağazanızdan yeni bir sipariş verildiğinde (Havale/EFT, Kapıda Ödeme veya Kredi Kartı/PayTR ile başarılı ödeme sonrası), sipariş ve müşteri detayları burada belirteceğiniz yönetici e-posta adreslerine anında iletilir.
            </p>

            <div className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  disabled={!isEditable}
                  className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                  checked={values['email.admin_order_notification_enabled'] !== 'false'}
                  onChange={(e) =>
                    updateField(
                      'email.admin_order_notification_enabled',
                      e.target.checked ? 'true' : 'false'
                    )
                  }
                />
                <div>
                  <span className="text-sm font-medium text-ink-900">
                    Yeni sipariş geldiğinde yöneticilere anlık e-posta bildirimi gönder
                  </span>
                  <p className="text-xs text-ink-400">
                    İşaret kaldırılırsa firma sahibine/yöneticiye sipariş bildirim e-postası gitmez; sadece müşteriye onay e-postası iletilir.
                  </p>
                </div>
              </label>

              {values['email.admin_order_notification_enabled'] !== 'false' && (
                <Field
                  label="Bildirim Alacak E-Posta Adresleri"
                  htmlFor="set-admin-order-emails"
                  hint="Birden fazla e-posta adresi yazabilirsiniz. Adresleri virgül (,), noktalı virgül (;) ile ayırabilir veya satır satır alt alta yazabilirsiniz. Örn: patron@hurmahane.com, siparis@hurmahane.com"
                >
                  <textarea
                    id="set-admin-order-emails"
                    rows={3}
                    disabled={!isEditable}
                    className="field font-mono text-sm"
                    value={values['email.admin_order_notification_emails'] ?? ''}
                    onChange={(e) =>
                      updateField('email.admin_order_notification_emails', e.target.value)
                    }
                    placeholder="patron@hurmahane.com, siparis@hurmahane.com"
                  />
                  {values['email.admin_order_notification_emails']?.trim() ? (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <span className="text-xs font-semibold text-ink-600">
                        Aktif Alıcılar ({
                          values['email.admin_order_notification_emails']
                            .split(/[,;\n\r]+/)
                            .map((e) => e.trim())
                            .filter((e) => e && e.includes('@')).length
                        }):
                      </span>
                      {values['email.admin_order_notification_emails']
                        .split(/[,;\n\r]+/)
                        .map((e) => e.trim())
                        .filter((e) => e && e.includes('@'))
                        .map((email, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 border border-amber-200"
                          >
                            <Mail className="h-3 w-3 text-amber-600" />
                            {email}
                          </span>
                        ))}
                    </div>
                  ) : (
                    <p className="mt-1.5 text-xs text-amber-600 font-medium">
                      ⚠️ Henüz bir e-posta adresi kaydedilmemiş. Bildirim alabilmek için lütfen en az bir geçerli e-posta adresi giriniz ve üstten kaydediniz.
                    </p>
                  )}
                </Field>
              )}
            </div>
          </section>

          {/* E-POSTA SABLONLARI KISAYOLU */}
          <section className="card p-6 border border-blue-200 bg-blue-50/40 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="mb-1 text-base font-semibold text-ink-900">
                E-Posta Şablonları & Görsel Tasarım Stüdyosu
              </h2>
              <p className="text-xs text-ink-600">
                Sipariş onayı, kargo takip, şifre sıfırlama ve terk edilen sepet gibi tüm e-postaların HTML tasarımını, resimlerini, logolarını ve <code>{'{Değişken}'}</code> alanlarını görsel editörde düzenleyin.
              </p>
            </div>
            <Link
              to="/eposta-sablonlari"
              className="btn-primary whitespace-nowrap inline-flex items-center gap-2 shrink-0"
            >
              <Mail className="h-4 w-4" />
              Şablonları Düzenle
            </Link>
          </section>

          {/* SİSTEM BİLDİRİM KURALLARI VE TETİKLEME */}
          <section className="card p-6">
            <h2 className="mb-1 text-base font-semibold text-ink-900">Sistem Bildirimleri ve Tetikleme Mekanizması</h2>
            <p className="mb-5 text-xs text-ink-500">
              Müşterilere iletilen bu temel sistem bildirimleri kritik e-ticaret süreçleri olduğu için her zaman aktiftir ve kapatılamaz. E-postalar arka plan zamanlayıcısı yerine doğrudan olay anında veya yönetici tarafından elle tetiklenir.
            </p>

            <div className="space-y-4">
              {/* 1. Sipariş Onayı */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600 shrink-0" />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-ink-900">Sipariş Alındı ve Onay Bildirimi</span>
                      <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-medium text-emerald-800">
                        Her Zaman Aktif · Olay Anında
                      </span>
                    </div>
                    <p className="text-xs text-ink-600 mt-1">
                      Müşteri ödemesini tamamlayıp sipariş oluşturduğu anda sistem tarafından anlık olarak sipariş özeti ve dekontu alıcının e-posta adresine gönderilir.
                    </p>
                  </div>
                </div>
                <span className="text-xs text-emerald-700 font-medium shrink-0 italic sm:text-right">Sipariş verildiğinde anlık</span>
              </div>

              {/* 2. Kargo Takibi */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600 shrink-0" />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-ink-900">Kargo ve Sevkiyat Takip Bildirimi</span>
                      <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-medium text-emerald-800">
                        Her Zaman Aktif · Kargo Çıktığında
                      </span>
                    </div>
                    <p className="text-xs text-ink-600 mt-1">
                      Sipariş paketlenip kargoya verildiğinde veya kargo takip numarası sisteme işlendiği anda kargo takip bağlantısı müşteriye anında iletilir.
                    </p>
                  </div>
                </div>
                <span className="text-xs text-emerald-700 font-medium shrink-0 italic sm:text-right">Kargoya verildiğinde anlık</span>
              </div>

              {/* 3. Terk Edilmiş Sepet */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-xl border border-amber-200 bg-amber-50/40 p-4">
                <div className="flex items-start gap-3">
                  <Mail className="mt-0.5 h-5 w-5 text-amber-600 shrink-0" />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-ink-900">Terk Edilmiş Sepet Hatırlatması</span>
                      <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-medium text-amber-800">
                        Yönetici Kontrolünde · Manuel Tetikleme
                      </span>
                    </div>
                    <p className="text-xs text-ink-600 mt-1">
                      Arka planda kontrolsüz otomatik gönderim yapılmaz. Yönetici panelindeki Pano ekranından sepetler incelenerek tek tek veya topluca hatırlatma e-postası tetiklenir.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    disabled={sendAllAbandonedMutation.isPending}
                    onClick={() => {
                      if (window.confirm('Bekleyen tüm terk edilmiş sepetlere hatırlatma e-postası göndermek istediğinize emin misiniz?')) {
                        sendAllAbandonedMutation.mutate();
                      }
                    }}
                    className="btn-secondary h-9 flex items-center gap-1.5 text-xs font-medium cursor-pointer"
                  >
                    <Send className="h-3.5 w-3.5 text-amber-600" />
                    <span>{sendAllAbandonedMutation.isPending ? 'Gönderiliyor...' : 'Şimdi Tetikle'}</span>
                  </button>
                  <Link
                    to="/"
                    className="btn-ghost h-9 flex items-center gap-1.5 text-xs font-medium text-ink-700"
                  >
                    <span>Panoda Gör</span>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* 5. SOSYAL MEDYA HESAPLARI */}
      {activeTab === 'social' && (
        <section className="card p-6">
          <h2 className="mb-1 text-base font-semibold text-ink-900">Sosyal Medya ve İletişim Kanalları</h2>
          <p className="mb-5 text-xs text-ink-500">
            Sitenizin altbilgi (footer) ve iletişim bölümlerinde yer alacak resmi hesap bağlantılarınız.
          </p>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Instagram Profil Bağlantısı" htmlFor="set-social-ig" hint="Örn: https://instagram.com/magazaniz">
              <input
                id="set-social-ig"
                type="text"
                disabled={!isEditable}
                className="field"
                value={values['social.instagram'] ?? ''}
                onChange={(e) => updateField('social.instagram', e.target.value)}
                placeholder="https://instagram.com/..."
              />
            </Field>

            <Field label="Facebook Sayfa Bağlantısı" htmlFor="set-social-fb" hint="Örn: https://facebook.com/sayfaniz">
              <input
                id="set-social-fb"
                type="text"
                disabled={!isEditable}
                className="field"
                value={values['social.facebook'] ?? ''}
                onChange={(e) => updateField('social.facebook', e.target.value)}
                placeholder="https://facebook.com/..."
              />
            </Field>

            <Field label="X (Twitter) Profil Bağlantısı" htmlFor="set-social-x" hint="Örn: https://x.com/magazaniz">
              <input
                id="set-social-x"
                type="text"
                disabled={!isEditable}
                className="field"
                value={values['social.x'] ?? ''}
                onChange={(e) => updateField('social.x', e.target.value)}
                placeholder="https://x.com/..."
              />
            </Field>

            <Field label="YouTube Kanal Bağlantısı" htmlFor="set-social-yt" hint="Örn: https://youtube.com/@kanaliniz">
              <input
                id="set-social-yt"
                type="text"
                disabled={!isEditable}
                className="field"
                value={values['social.youtube'] ?? ''}
                onChange={(e) => updateField('social.youtube', e.target.value)}
                placeholder="https://youtube.com/@..."
              />
            </Field>

            <Field label="WhatsApp Destek Hattı Numarası" htmlFor="set-social-wa" hint="Uluslararası formatta, örn: +905551234567">
              <input
                id="set-social-wa"
                type="text"
                disabled={!isEditable}
                className="field"
                value={values['social.whatsapp'] ?? ''}
                onChange={(e) => updateField('social.whatsapp', e.target.value)}
                placeholder="+905551234567"
              />
            </Field>
          </div>
        </section>
      )}

      {/* 6. ARAMA MOTORU VE TAKİP (SEO & ANALİTİK) */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          <section className="card p-6">
            <h2 className="mb-1 text-base font-semibold text-ink-900">Arama Motoru Optimizasyonu (SEO)</h2>
            <p className="mb-5 text-xs text-ink-500">
              Google ve diğer arama motorları için genel mağaza meta tanımları.
            </p>

            <div className="grid gap-5">
              <Field label="Varsayılan Sayfa Başlığı (Title)" htmlFor="set-seo-title" hint="Özel başlık girilmemiş sayfalarda bu başlık kullanılır">
                <input
                  id="set-seo-title"
                  type="text"
                  disabled={!isEditable}
                  className="field"
                  value={values['seo.default_title'] ?? ''}
                  onChange={(e) => updateField('seo.default_title', e.target.value)}
                  placeholder="E-Ticaret | Kaliteli ve Güvenilir Alışveriş"
                />
              </Field>

              <Field label="Varsayılan Meta Açıklaması (Description)" htmlFor="set-seo-desc" hint="Arama sonuçlarında başlığın altında çıkan açıklama metni">
                <textarea
                  id="set-seo-desc"
                  rows={2}
                  disabled={!isEditable}
                  className="field"
                  value={values['seo.default_description'] ?? ''}
                  onChange={(e) => updateField('seo.default_description', e.target.value)}
                  placeholder="En yeni ürünler, avantajlı fiyatlar ve hızlı kargo fırsatlarıyla güvenli alışveriş."
                />
              </Field>

              <Field
                label="Google Site Doğrulama Kodu (Search Console & Merchant Center)"
                htmlFor="set-seo-gverify"
                hint="Google'ın verdiği doğrulama kodunu veya <meta name=&quot;google-site-verification&quot; ... /> etiketini doğrudan yapıştırabilirsiniz"
              >
                <input
                  id="set-seo-gverify"
                  type="text"
                  disabled={!isEditable}
                  className="field font-mono"
                  value={values['seo.google_site_verification'] ?? ''}
                  onChange={(e) => updateField('seo.google_site_verification', e.target.value)}
                  placeholder="Örn: xYz123AbC456... veya <meta name=&quot;google-site-verification&quot; content=&quot;...&quot; />"
                />
              </Field>

              <label className="flex items-center gap-3 cursor-pointer pt-1">
                <input
                  type="checkbox"
                  disabled={!isEditable}
                  className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                  checked={values['seo.robots_index'] !== 'false'}
                  onChange={(e) => updateField('seo.robots_index', String(e.target.checked))}
                />
                <div>
                  <span className="text-sm font-medium text-ink-900">Arama Motorlarının İndekslemesine İzin Ver</span>
                  <p className="text-xs text-ink-400">Kapalı olduğunda arama motorlarına 'indeksleme' talimatı verilir.</p>
                </div>
              </label>
            </div>
          </section>

          <section className="card p-6">
            <h2 className="mb-1 text-base font-semibold text-ink-900">Ziyaretçi ve Dönüşüm Takip Kodları</h2>
            <p className="mb-5 text-xs text-ink-500">
              Google ve Meta reklam hesaplarınız için tanımlayıcı kimlik kodları. Kodları girdiğinizde siteye otomatik entegre edilir ve tüm e-ticaret olayları (ürün görüntüleme, sepete ekleme, ödeme başlatma, satın alma) otomatik gönderilir.
            </p>

            <div className="grid gap-5 md:grid-cols-3">
              <Field
                label="Google Analytics 4 Ölçüm Kimliği"
                htmlFor="set-ga-id"
                hint="G-XXXXXXXXXX formatında kimlik"
              >
                <input
                  id="set-ga-id"
                  type="text"
                  disabled={!isEditable}
                  className="field font-mono"
                  value={values['analytics.ga_id'] ?? ''}
                  onChange={(e) => updateField('analytics.ga_id', e.target.value)}
                  placeholder="G-ABC123XYZ"
                />
              </Field>

              <Field
                label="Google Etiket Yöneticisi Konteyner Kodu"
                htmlFor="set-gtm-id"
                hint="GTM-XXXXXXX formatında kod"
              >
                <input
                  id="set-gtm-id"
                  type="text"
                  disabled={!isEditable}
                  className="field font-mono"
                  value={values['analytics.gtm_id'] ?? ''}
                  onChange={(e) => updateField('analytics.gtm_id', e.target.value)}
                  placeholder="GTM-123456"
                />
              </Field>

              <Field
                label="Meta Pixel Tanımlayıcısı"
                htmlFor="set-pixel-id"
                hint="Meta Reklam Yöneticisi Piksel Numarası"
              >
                <input
                  id="set-pixel-id"
                  type="text"
                  disabled={!isEditable}
                  className="field font-mono"
                  value={values['analytics.meta_pixel_id'] ?? ''}
                  onChange={(e) => updateField('analytics.meta_pixel_id', e.target.value)}
                  placeholder="123456789012345"
                />
              </Field>
            </div>
          </section>

          <section className="card p-6">
            <h2 className="mb-1 text-base font-semibold text-ink-900">
              Google Otomatik Veri Besleme Bağlantıları (Canlı XML Feeds)
            </h2>
            <p className="mb-5 text-xs text-ink-500">
              Google Search Console ve Google Merchant Center (Alışveriş) hesaplarınıza bir kez tanımladığınızda; yeni eklediğiniz ürünler, fiyat/stok değişimleri ve onaylanan müşteri yorumları Google'a <strong>otomatik</strong> aktarılır.
            </p>

            <div className="space-y-4">
              {[
                {
                  title: '1. Google Search Console — Otomatik Site Haritası (Sitemap)',
                  desc: 'Tüm aktif ürünlerinizi, kategorilerinizi, markalarınızı ve blog yazılarınızı Google Arama dizinine otomatik bildirir.',
                  url: 'https://hurmahane.com/sitemap.xml',
                },
                {
                  title: '2. Google Merchant Center — Otomatik Ürün ve Stok Beslemesi (Shopping Feed)',
                  desc: 'Google Alışveriş sekmesinde ürünlerinizin fotoğraf, fiyat, indirimli fiyat ve stok durumuyla otomatik listelenmesini sağlar.',
                  url: 'https://hurmahane.com/google-shopping.xml',
                },
                {
                  title: '3. Google Merchant Center — Otomatik Ürün Yorumları ve Yıldız Puanları (Product Ratings Feed)',
                  desc: 'Müşterilerinizin yazdığı ve onayladığınız tüm ürün değerlendirmelerini Google Alışveriş yıldızlarına otomatik aktarır.',
                  url: 'https://hurmahane.com/google-reviews.xml',
                },
              ].map((feed) => (
                <div
                  key={feed.url}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-ink-200 bg-ink-50/50 p-4"
                >
                  <div className="space-y-0.5">
                    <div className="text-sm font-semibold text-ink-900">{feed.title}</div>
                    <p className="text-xs text-ink-500">{feed.desc}</p>
                    <code className="mt-1 inline-block rounded bg-white px-2 py-0.5 text-xs font-mono text-brand-700 border border-ink-200">
                      {feed.url}
                    </code>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(feed.url);
                      toast.success('Bağlantı kopyalandı.');
                    }}
                    className="btn-secondary shrink-0 flex items-center gap-1.5 text-xs"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    <span>Adresi Kopyala</span>
                  </button>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
